// Feature: net-worth-dashboard
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, BarChart3 } from "lucide-react";
import type { AssetCategory } from "@/hooks/useNetWorthData";

interface AssetBreakdownCardProps {
  categories: AssetCategory[];
  totalAssets: number;
  displayCurrency: string;
  isLoading: boolean;
  ratesStale?: boolean;
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function AssetBreakdownCard({
  categories,
  totalAssets,
  displayCurrency,
  isLoading,
  ratesStale = false,
}: AssetBreakdownCardProps) {
  return (
    <Card className="shadow-card hover:shadow-card-hover transition-shadow duration-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-success" aria-hidden="true" />
            Asset Breakdown
          </CardTitle>
          {ratesStale && (
            <Badge
              variant="outline"
              className="flex items-center gap-1 text-warning border-warning/40"
              aria-label="Exchange rates may be stale"
            >
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              Rates may be stale
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-3" aria-label="Loading asset breakdown">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <Skeleton className="h-3.5 w-28" />
                  <Skeleton className="h-3.5 w-20" />
                </div>
                <Skeleton className="h-1.5 w-full rounded-full" />
              </div>
            ))}
            <Separator />
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
            </div>
          </div>
        ) : (
          <>
            <ul className="space-y-3" aria-label="Asset categories">
              {categories.map((category) => {
                const pct = totalAssets > 0 ? (category.total / totalAssets) * 100 : 0;
                return (
                  <li key={category.label} className="space-y-1">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">
                        {category.label}
                      </span>
                      <span className="font-medium tabular-nums">
                        {formatCurrency(category.total, displayCurrency)}
                      </span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-success/70 transition-all duration-500"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
            <Separator />
            <div className="flex justify-between items-center font-semibold">
              <span>Total Assets</span>
              <span className="tabular-nums text-success">
                {formatCurrency(totalAssets, displayCurrency)}
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
